/**
 * R-67 — Centralized unit normalization for internal number consistency.
 */
import type { CanonicalFactKey } from '../../types/auditCanonicalFacts';

export const MI_TO_KM = 1.60934;
export const GW_TO_MW = 1000;
export const HA_TO_ACRES = 2.47105;
export const MONTHS_PER_YEAR = 12;

export type InternalCanonicalKey =
  | 'capacity_mw'
  | 'interconnection_distance_km'
  | 'nuclear_proximity_km'
  | 'flood_zone'
  | 'voltage_kv'
  | 'acreage'
  | 'wetland_percent'
  | 'nearest_substation'
  | 'grid_operator'
  | 'water_source_type'
  | 'census_tract';

const KEY_MAP: Record<CanonicalFactKey, InternalCanonicalKey> = {
  capacityMW: 'capacity_mw',
  interconnectionDistanceKm: 'interconnection_distance_km',
  nuclearProximityKm: 'nuclear_proximity_km',
  floodZone: 'flood_zone',
  voltageKV: 'voltage_kv',
  acreage: 'acreage',
  wetlandPercent: 'wetland_percent',
  nearestSubstation: 'nearest_substation',
  gridOperator: 'grid_operator',
  waterSourceType: 'water_source_type',
  censusTract: 'census_tract',
};

export function toInternalCanonicalKey(key: CanonicalFactKey): InternalCanonicalKey {
  return KEY_MAP[key];
}

export function parseDistanceKm(raw: string, unit?: string): number | null {
  const n = parseFloat(raw.replace(/,/g, ''));
  if (!Number.isFinite(n)) return null;
  const u = (unit ?? 'km').toLowerCase();
  if (/^mi|mile/.test(u)) return n * MI_TO_KM;
  return n;
}

export function parseCapacityMw(raw: string, unit?: string): number | null {
  const n = parseFloat(raw.replace(/,/g, ''));
  if (!Number.isFinite(n)) return null;
  const u = (unit ?? 'mw').toLowerCase();
  if (u === 'gw') return n * GW_TO_MW;
  if (/megawatt/.test(u)) return n;
  return n;
}

export function parseAreaAcres(raw: string, unit?: string): number | null {
  const n = parseFloat(raw.replace(/,/g, ''));
  if (!Number.isFinite(n)) return null;
  const u = (unit ?? 'ac').toLowerCase();
  if (/^ha|hectare/.test(u)) return n * HA_TO_ACRES;
  return n;
}

/**
 * Durations normalize to YEARS — the store's display unit for duration facts
 * (`grid.queueYears`). Prose quotes interconnection queues in months far more
 * often than in years ("12-24 months"), so the conversion happens here rather
 * than at each call site.
 */
export function parseDurationYears(raw: string, unit?: string): number | null {
  const n = parseFloat(raw.replace(/,/g, ''));
  if (!Number.isFinite(n)) return null;
  const u = (unit ?? 'years').toLowerCase();
  if (/^mo/.test(u)) return n / MONTHS_PER_YEAR;
  return n;
}

export function normalizeFloodZone(raw: string): string {
  return raw.trim().toUpperCase().replace(/^ZONE\s+/, '');
}

/** Normalize raw value + units to canonical numeric/string form for comparison. */
export function normalizeToCanonical(
  value: string | number,
  units: string | undefined,
  canonicalKey: InternalCanonicalKey | CanonicalFactKey,
): number | string {
  const key = canonicalKey.includes('_') || !/[A-Z]/.test(canonicalKey)
    ? canonicalKey as InternalCanonicalKey
    : toInternalCanonicalKey(canonicalKey as CanonicalFactKey);

  if (typeof value === 'string' && key === 'flood_zone') {
    return normalizeFloodZone(value);
  }

  if (typeof value === 'number') {
    switch (key) {
      case 'capacity_mw':
      case 'interconnection_distance_km':
      case 'nuclear_proximity_km':
      case 'voltage_kv':
      case 'acreage':
      case 'wetland_percent':
        return value;
      default:
        return value;
    }
  }

  const raw = String(value).replace(/,/g, '');
  switch (key) {
    case 'capacity_mw': {
      const u = (units ?? raw.match(/(MW|GW|megawatts?)/i)?.[1] ?? 'mw').toLowerCase();
      const num = parseFloat(raw.replace(/[^\d.]/g, ''));
      const v = parseCapacityMw(String(num), u);
      return v ?? raw;
    }
    case 'interconnection_distance_km':
    case 'nuclear_proximity_km': {
      const u = (units ?? raw.match(/(km|mi|miles?|kilometers?)/i)?.[1] ?? 'km').toLowerCase();
      const num = parseFloat(raw.replace(/[^\d.]/g, ''));
      const v = parseDistanceKm(String(num), u);
      return v ?? raw;
    }
    case 'voltage_kv': {
      const num = parseFloat(raw.replace(/[^\d.]/g, ''));
      return Number.isFinite(num) ? num : raw;
    }
    case 'acreage': {
      const u = (units ?? raw.match(/(acres?|ac\.?|ha|hectares?)/i)?.[1] ?? 'ac').toLowerCase();
      const num = parseFloat(raw.replace(/[^\d.]/g, ''));
      const v = parseAreaAcres(String(num), u);
      return v ?? raw;
    }
    case 'wetland_percent': {
      const num = parseFloat(raw.replace(/[^\d.]/g, ''));
      return Number.isFinite(num) ? num : raw;
    }
    case 'flood_zone':
      return normalizeFloodZone(raw);
    default:
      return String(value).trim();
  }
}

/** Tolerance check between two normalized values for a canonical key. */
export function valuesWithinTolerance(
  key: InternalCanonicalKey | CanonicalFactKey,
  a: number | string,
  b: number | string,
): boolean {
  const internalKey = typeof key === 'string' && key.includes('_')
    ? key as InternalCanonicalKey
    : toInternalCanonicalKey(key as CanonicalFactKey);

  if (typeof a === 'string' || typeof b === 'string') {
    return String(a).trim().toUpperCase() === String(b).trim().toUpperCase();
  }

  switch (internalKey) {
    case 'capacity_mw':
      return Math.abs(a - b) / Math.max(Math.abs(a), Math.abs(b), 1) <= 0.01;
    case 'interconnection_distance_km':
    case 'nuclear_proximity_km':
      return Math.abs(a - b) / Math.max(Math.abs(a), Math.abs(b), 1) <= 0.02;
    case 'wetland_percent':
      return Math.abs(a - b) <= 0.5;
    case 'voltage_kv':
    case 'flood_zone':
      return a === b;
    case 'acreage':
      return Math.abs(a - b) / Math.max(a, b, 1) <= 0.05;
    default:
      return a === b;
  }
}
